import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_DESTINATIONS,
  invalidateCampaignQueries,
} from '#/entities/campaign'
import type {
  CampaignContactRow,
  CampaignDestination,
} from '#/entities/campaign'
import { CampaignTab } from '#/shared/api/generated/communication/models'
import type {
  CampaignCreate,
  CampaignPeopleAdd,
  EnrollmentContact,
} from '#/shared/api/generated/communication/models'
import {
  useAddPeopleApiV1CampaignsCampaignIdPeoplePost,
  useCreateCampaignApiV1CampaignsPost,
  useListCampaignRowsApiV1CampaignsRowsGet,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'
import type { SelectOption } from '#/shared/ui/select'

/** One page of campaigns is enough to pick from; the box also searches. */
const CAMPAIGN_OPTIONS_SIZE = 100

/**
 * Ticked rows as the enrolment body wants them.
 *
 * A row without a `customer_id` cannot be enrolled anywhere — there is no
 * person behind it to carry into another campaign — so it is dropped here
 * rather than sent as a contact with no identity. `shipment_id` rides along so
 * the new campaign chases the same quote and inherits its merge fields.
 */
export function toEnrollmentContacts(
  rows: CampaignContactRow[],
): EnrollmentContact[] {
  return rows.flatMap((row) =>
    row.customerId == null
      ? []
      : [{ customer_id: row.customerId, shipment_id: row.shipmentId }],
  )
}

/**
 * Campaigns to send people to. Fetched only once the dialog is open *and*
 * pointed at an existing campaign — picking "a new campaign" never needs the
 * list, so it never asks for it.
 */
function useCampaignOptions(enabled: boolean, excludeCampaignId?: string) {
  const query = useListCampaignRowsApiV1CampaignsRowsGet(
    { tab: CampaignTab.all, page: 1, size: CAMPAIGN_OPTIONS_SIZE },
    { query: { enabled } },
  )
  const options: SelectOption[] = useMemo(
    () =>
      (query.data?.data.rows ?? [])
        // Somebody cannot be added to the campaign they are being taken from.
        .filter((row) => row.id !== excludeCampaignId)
        .map((row) => ({ value: row.id, label: row.name })),
    [query.data, excludeCampaignId],
  )
  return { options, isLoadingOptions: query.isLoading }
}

interface AddToCampaignOptions {
  /** Who joins: ticked contacts, or a quote-list selection the shipment
   *  service resolves. Exactly what `POST /campaigns/{id}/people` takes. */
  people: CampaignPeopleAdd
  /** The campaign the people are being taken *out* of, so it is not offered. */
  excludeCampaignId?: string
  enabled: boolean
  onAdded: () => void
}

/** Whether there is anybody to enrol at all. */
export function hasPeople(people: CampaignPeopleAdd): boolean {
  if (people.shipment_selection != null) return true
  return (people.contacts?.length ?? 0) > 0
}

/**
 * "Add to a campaign" — the same people, enrolled somewhere else.
 *
 * `POST /campaigns/{id}/people` takes the people directly, so an existing
 * campaign is one call. A new one is two, in order: the campaign has to exist
 * before anybody can be put in it, and a create that succeeds followed by an
 * enrolment that fails leaves a real empty campaign rather than a half-written
 * one — which is why the failure is reported rather than swallowed.
 */
export function useAddToCampaign({
  people,
  excludeCampaignId,
  enabled,
  onAdded,
}: AddToCampaignOptions) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [destination, setDestination] = useState<CampaignDestination>(
    CAMPAIGN_DESTINATIONS.existing,
  )
  const [campaignId, setCampaignId] = useState('')
  const [name, setName] = useState('')

  const { options, isLoadingOptions } = useCampaignOptions(
    enabled && destination === CAMPAIGN_DESTINATIONS.existing,
    excludeCampaignId,
  )

  const fail = useCallback(
    (error: unknown) =>
      toast.error(
        getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
      ),
    [t],
  )

  const addPeople = useAddPeopleApiV1CampaignsCampaignIdPeoplePost({
    mutation: {
      onSuccess: (response) => {
        toast.success(
          t('campaigns.modal.add.toasts.added', { count: response.data.added }),
        )
        invalidateCampaignQueries(queryClient)
        onAdded()
      },
      onError: fail,
    },
  })

  const createCampaign = useCreateCampaignApiV1CampaignsPost({
    mutation: {
      onSuccess: (response) =>
        addPeople.mutate({ campaignId: response.data.id, data: people }),
      onError: fail,
    },
  })

  const onSubmit = useCallback(
    (create: CampaignCreate) => {
      if (destination === CAMPAIGN_DESTINATIONS.existing) {
        addPeople.mutate({ campaignId, data: people })
        return
      }
      createCampaign.mutate({ data: create })
    },
    [destination, addPeople, createCampaign, campaignId, people],
  )

  return {
    destination,
    setDestination,
    campaignId,
    setCampaignId,
    name,
    setName,
    options,
    isLoadingOptions,
    onSubmit,
    isAdding: addPeople.isPending || createCampaign.isPending,
    /** Nothing to enrol, or nowhere to put them. */
    canSubmit:
      hasPeople(people) &&
      (destination === CAMPAIGN_DESTINATIONS.existing
        ? campaignId !== ''
        : name.trim() !== ''),
  }
}

export type UseAddToCampaignResult = ReturnType<typeof useAddToCampaign>
